import React, { useState, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare, faSpinner } from "@fortawesome/free-solid-svg-icons";
import type { DevToolsPlugin } from "../../types";
import { Accordion } from "../../Accordion";

interface AutofillPluginProps {
  journeyId?: string;
  site?: string;
  answerSets?: Record<string, [string, string][]>;
  fillJourneyAnswersPath?: string;
  fillCustomJourneyAnswersPath?: string;
}

function csrfToken(): string {
  return (
    document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content") || ""
  );
}

function formatGroupLabel(label: string): string {
  const parts = label.split(" / ");
  if (parts.length === 2 && parts[0] === parts[1]) return parts[0];
  return parts.join(" - ");
}

function AutofillPanel({
  journeyId,
  site,
  answerSets = {},
  fillJourneyAnswersPath = "/fill_journey_answers",
  fillCustomJourneyAnswersPath = "/fill_custom_journey_answers",
}: AutofillPluginProps) {
  const [customJson, setCustomJson] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitForm = useCallback(
    (endpoint: string, key: string, value: string) => {
      if (!journeyId) return;
      setIsSubmitting(true);

      const formData = new FormData();
      formData.append("journey_id", journeyId);
      if (site) formData.append("site", site);
      formData.append(key, value);

      fetch(endpoint, {
        method: "POST",
        body: formData,
        headers: { "X-CSRF-Token": csrfToken() },
      })
        .then((response) => {
          if (response.redirected) {
            window.location.href = response.url;
          }
        })
        .catch((error) => console.error("Autofill error:", error))
        .finally(() => setIsSubmitting(false));
    },
    [journeyId, site],
  );

  if (!journeyId) {
    return (
      <div className="dt-status-banner dt-status-banner--warning">
        <div className="dt-status-banner__title">No journey active</div>
        <div className="dt-status-banner__text">
          Navigate to a journey page to use autofill
        </div>
      </div>
    );
  }

  return (
    <>
      {Object.entries(answerSets).map(([groupLabel, options]) => (
        <Accordion key={groupLabel} title={formatGroupLabel(groupLabel)} count={options.length}>
          <div className="dt-chip-grid">
            {options.map(([label, value]) => (
              <button
                key={value}
                className="dt-chip"
                disabled={isSubmitting}
                onClick={() =>
                  submitForm(fillJourneyAnswersPath, "answer_set", value)
                }
                type="button"
              >
                {label}
              </button>
            ))}
          </div>
        </Accordion>
      ))}

      <hr className="dt-autofill__divider" />

      <div className="dt-section-label">Custom JSON</div>
      <div className="dt-form-field">
        <textarea
          className="dt-form-textarea"
          placeholder="Enter answer set in JSON format..."
          value={customJson}
          onChange={(e) => setCustomJson(e.target.value)}
        />
      </div>
      <button
        className="dt-btn dt-btn--primary"
        onClick={() =>
          submitForm(
            fillCustomJourneyAnswersPath,
            "custom_answer_set",
            customJson,
          )
        }
        disabled={!customJson || isSubmitting}
        type="button"
      >
        {isSubmitting ? "Submitting..." : "Submit Custom Answers"}
      </button>

      {isSubmitting && (
        <div className="dt-autofill__overlay" aria-busy="true" aria-live="polite">
          <FontAwesomeIcon icon={faSpinner} spin className="dt-autofill__spinner" />
        </div>
      )}
    </>
  );
}

export function autofillPlugin(props: AutofillPluginProps): DevToolsPlugin {
  return {
    id: "autofill",
    name: "Autofill",
    icon: faPenToSquare,
    render: () => <AutofillPanel {...props} />,
  };
}
